/**
 * Regras da aba FC (planilha Caixa Cidadania) — distribuição de lucros entre sócios.
 * Percentuais padrão e fórmulas de Saldo Atual conforme planilha.
 */

export interface SocioParticipacao {
  id: number;
  nome: string;
  /** Fração 0–1 (ex.: 0.6 = 60%). */
  percentual: number;
  /** Saldo anterior do primeiro mês do ano (col C da planilha). */
  saldoInicial: number;
}

export interface AjusteSocios {
  label: string;
  valor: number;
}

/** Entradas editáveis por mês (índice 0 = janeiro). */
export interface SociosMesInput {
  /** Se null, usa Salários÷12 do fluxo quando disponível. */
  retiradaReservaManual: number | null;
  /** Se null, usa Resultado Mensal − Retirada reserva. */
  participacaoManual: number | null;
  aluguelValor: number;
  /** Retiradas manuais; se null na posição, usa PLR do fluxo. */
  retiradasManual: Array<number | null>;
  ajustes: AjusteSocios[];
}

export interface SociosMesCalc {
  retiradaReserva: number;
  participacao: number;
  saldoAnterior: number[];
  distribuicao: number[];
  retiradas: number[];
  aluguelValor: number;
  aluguelSocio: number[];
  ajustes: AjusteSocios[];
  /** null quando a planilha deixa a célula em branco (sem distribuição/reserva). */
  saldoAtual: Array<number | null>;
  lucrosRetidos: number | null;
}

export const SOCIOS_PADRAO: SocioParticipacao[] = [
  { id: 1, nome: 'Socio 1', percentual: 0.6, saldoInicial: 35000 },
  { id: 2, nome: 'Socio 2', percentual: 0.225, saldoInicial: 25000 },
  { id: 3, nome: 'Socio 3', percentual: 0.125, saldoInicial: 27779.45 },
  { id: 4, nome: 'Socio 4', percentual: 0.05, saldoInicial: 25000 },
];

export const RESERVA_SALDO_INICIAL_PADRAO = 6052.15;

export function criarMesInputVazio(): SociosMesInput {
  return {
    retiradaReservaManual: null,
    participacaoManual: null,
    aluguelValor: 0,
    retiradasManual: [null, null, null, null],
    ajustes: [
      { label: 'teste 1', valor: 0 },
      { label: 'teste 2', valor: 0 },
      { label: 'teste 3', valor: 0 },
      { label: 'teste 4', valor: 0 },
    ],
  };
}

export function criarAnoInputs(): SociosMesInput[] {
  return Array.from({ length: 12 }, () => criarMesInputVazio());
}

/**
 * Calcula um mês conforme FC:
 * - Dist = Participação × %
 * - Aluguel sócio = Valor × %
 * - Saldo Socio1 = Ant + Dist − Ret + Alug2 + Alug3 + Alug4 + Aj1
 * - Saldo SocioN = Ant + Dist − Ret − AlugN + AjN  (N=2..4)
 * - Reserva = Ant + RetiradaReserva (se RetiradaReserva ≥ 1)
 */
export function calcularMesSocios(
  socios: SocioParticipacao[],
  reservaInicial: number,
  input: SociosMesInput,
  resultadoMensal: number,
  salariosMes: number,
  plrPorSocio: number[],
  saldoAnteriorRef: number[] | null
): SociosMesCalc {
  const retiradaAuto = Number(salariosMes || 0) / 12;
  const retiradaReserva =
    input.retiradaReservaManual != null
      ? Number(input.retiradaReservaManual || 0)
      : retiradaAuto;

  const participacaoAuto = Number(resultadoMensal || 0) - retiradaReserva;
  const participacao =
    input.participacaoManual != null
      ? Number(input.participacaoManual || 0)
      : participacaoAuto;

  const saldoAnterior: number[] = socios.map((s, i) => {
    if (saldoAnteriorRef && saldoAnteriorRef.length > i) {
      return Number(saldoAnteriorRef[i] || 0);
    }
    return Number(s.saldoInicial || 0);
  });

  const saldoAnteriorReserva =
    saldoAnteriorRef && saldoAnteriorRef.length > 4
      ? Number(saldoAnteriorRef[4] || 0)
      : Number(reservaInicial || 0);

  const temParticipacao = Math.abs(participacao) > 0.0001;
  const distribuicao = socios.map((s) =>
    temParticipacao ? participacao * Number(s.percentual || 0) : 0
  );

  const retiradas = socios.map((_, i) => {
    const manual = input.retiradasManual[i];
    if (manual != null) return Number(manual || 0);
    const plr = Number(plrPorSocio[i] || 0);
    return plr < 0.1 ? 0 : plr;
  });

  const aluguelValor = Number(input.aluguelValor || 0);
  const aluguelSocio = socios.map((s) =>
    aluguelValor < 0.1 ? 0 : aluguelValor * Number(s.percentual || 0)
  );

  const ajustes = (input.ajustes || []).map((a) => ({
    label: a.label || '',
    valor: Number(a.valor || 0),
  }));
  while (ajustes.length < 4) {
    ajustes.push({ label: `teste ${ajustes.length + 1}`, valor: 0 });
  }

  const saldoAtual: Array<number | null> = [null, null, null, null, null];

  if (temParticipacao) {
    // Socio 1 recebe a cota de aluguel dos demais (padrão planilha Claudia)
    saldoAtual[0] =
      saldoAnterior[0] +
      distribuicao[0] -
      retiradas[0] +
      aluguelSocio[1] +
      aluguelSocio[2] +
      aluguelSocio[3] +
      Number(ajustes[0].valor || 0);

    for (let i = 1; i < 4; i++) {
      saldoAtual[i] =
        saldoAnterior[i] +
        distribuicao[i] -
        retiradas[i] -
        aluguelSocio[i] +
        Number(ajustes[i].valor || 0);
    }
  }

  if (retiradaReserva >= 1) {
    saldoAtual[4] = saldoAnteriorReserva + retiradaReserva;
  }

  const saldosValidos = saldoAtual.filter((v) => v != null) as number[];
  const lucrosRetidos = saldosValidos.length
    ? saldosValidos.reduce((a, b) => a + b, 0)
    : null;

  return {
    retiradaReserva,
    participacao,
    saldoAnterior: [...saldoAnterior, saldoAnteriorReserva],
    distribuicao,
    retiradas,
    aluguelValor,
    aluguelSocio,
    ajustes,
    saldoAtual,
    lucrosRetidos,
  };
}

export function recalcularAnoSocios(
  socios: SocioParticipacao[],
  reservaInicial: number,
  inputs: SociosMesInput[],
  resultadosMensais: number[],
  salariosPorMes: number[],
  plrPorMesPorSocio: number[][]
): SociosMesCalc[] {
  const out: SociosMesCalc[] = [];
  let saldoConhecido: number[] | null = null;

  for (let m = 0; m < 12; m++) {
    const calc = calcularMesSocios(
      socios,
      reservaInicial,
      inputs[m] || criarMesInputVazio(),
      Number(resultadosMensais[m] || 0),
      Number(salariosPorMes[m] || 0),
      plrPorMesPorSocio[m] || [0, 0, 0, 0],
      saldoConhecido
    );
    out.push(calc);

    if (!saldoConhecido) {
      saldoConhecido = [...calc.saldoAnterior];
    }
    calc.saldoAtual.forEach((v, i) => {
      if (v != null) {
        saldoConhecido![i] = v;
      }
    });
  }
  return out;
}
